/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { AuthenticationTypeV3 } from "@tivocorp/data-domain-lib";
import type {
  AuthConfigurationV3,
  DomainToken
} from "@tivocorp/data-domain-lib";

import { DeviceModel } from "../device";
import { Log } from "../logging";
import { SignInInteractor } from "../interactor";
import { buildPrimaryCredentialJwt } from "../utility";
import { RetryMechanism } from "../retrymechanism";
import { createSilentRetryOptions } from "../retrymechanism/silentRetryOptions";
import type { HttpMethod } from "../types";
import type { ITokenRefreshStrategy } from "./ITokenRefreshStrategy";
import {
  resolveAuthorizationSchemes,
  resolveDbsSchemes,
  resolveDomainTokenScheme,
  resolveIpAutoLoginAuthenticationUrl,
  type TAuthorizationSchemes
} from "./authSchemes";

const TAG: string = "auth/DomainTokenRefreshStrategy";

/**
 * Runs one refresh call under the silent retry ladder (1s, 3s, 5s — about 9s
 * at most). A refresh runs unseen — behind a blank boot screen, or ahead of
 * an authenticated request — and every path has a fallback, so a transient
 * failure is retried briefly rather than ending the path, and a definitive one
 * (400 / 401 / 403 / 404) is not retried at all.
 *
 * Without it one dropped packet on the last path failed the whole refresh and,
 * at boot, sent a signed-in user to the sign-in screen.
 */
function callWithSilentRetry<T>(
  call: () => Promise<T>,
  httpMethod: HttpMethod
): Promise<T> {
  return RetryMechanism.callServiceWithRetry<T>(
    call,
    [],
    createSilentRetryOptions(httpMethod)
  );
}

/**
 * Mints domain tokens against the domain-tokens service.
 *
 * Two distinct exchanges, because an account token and a profile token are not
 * obtained the same way:
 *
 * - **Account** (`profileId === null`): a walk down the re-authentication
 *   paths the device could have signed in with — IP auto-login, then the
 *   bound-device shortcut, then a full DBS re-bind. Each step is tried only if
 *   the previous one is inapplicable or fails.
 * - **Profile**: `/domainToken` alone, carrying the profile ID as the `spid`
 *   claim on the primary credential. The device's public key is already
 *   registered — `/claim` is part of sign-in, which necessarily happened
 *   before any profile could be picked — so a profile switch is one call.
 *
 * Both start by fetching the auth configurations, so the authorization
 * schemes on every request are the ones the auth-config service advertises
 * now, not the ones persisted at the last sign-in. If that fetch fails the
 * refresh still runs, on the persisted schemes
 * ({@link fetchAuthConfigurations}).
 *
 * Every service call runs under the silent retry ladder
 * ({@link callWithSilentRetry}), so a transient failure is retried briefly
 * before a path gives up and the next one is tried.
 *
 * Purely an exchange: nothing here writes to storage. {@link TokenManager}
 * persists whatever comes back, so a token can never be minted and then lost.
 */
export class DomainTokenRefreshStrategy implements ITokenRefreshStrategy {
  public async refresh(profileId: string | null): Promise<DomainToken> {
    const signInInteractor: SignInInteractor = new SignInInteractor();
    const configs: AuthConfigurationV3[] | undefined =
      await this.fetchAuthConfigurations(signInInteractor);
    if (profileId !== null && profileId.length > 0) {
      return this.refreshProfileToken(signInInteractor, configs, profileId);
    }
    return this.refreshAccountToken(signInInteractor, configs);
  }

  /**
   * Fetches the auth configurations the refresh reads its authorization
   * schemes from. Returns undefined when the service cannot be reached: the
   * scheme resolvers then fall back to the context persisted at sign-in, so
   * an auth-config outage alone does not fail a refresh.
   */
  private async fetchAuthConfigurations(
    signInInteractor: SignInInteractor
  ): Promise<AuthConfigurationV3[] | undefined> {
    try {
      Log.d(TAG, "refresh() fetching auth configurations");
      const response = await callWithSilentRetry(
        () =>
          signInInteractor.fetchAuthConfigurationsV3({
            msoName: DeviceModel.getTiVoSettings().getMsoName()
          }),
        "GET"
      );
      if (
        response === undefined ||
        response === null ||
        response.authconfigurations === undefined ||
        response.authconfigurations === null
      ) {
        Log.w(TAG, "refresh() auth configurations response carried none");
        return undefined;
      }
      return response.authconfigurations;
    } catch (error: unknown) {
      Log.e(
        TAG,
        "refresh() auth configurations fetch failed, using persisted schemes",
        error
      );
      return undefined;
    }
  }

  /**
   * Profile-scoped mint: `/domainToken` alone, with `spid` on the primary
   * credential.
   *
   * No `/claim` here. `/claim` registers the device's public key with the
   * domain-tokens service — a device-level, once-per-sign-in registration that
   * all three sign-in flows already perform before the profile gate is ever
   * reached. Re-registering the same key with an `spid` attached buys nothing
   * and puts a second network round trip in front of every profile switch.
   */
  private async refreshProfileToken(
    signInInteractor: SignInInteractor,
    configs: AuthConfigurationV3[] | undefined,
    profileId: string
  ): Promise<DomainToken> {
    Log.d(TAG, "refresh() minting profile-scoped token");

    const domainTokenScheme: string = await resolveDomainTokenScheme(configs);
    const primaryCredential: string =
      await buildPrimaryCredentialJwt(profileId);
    const domainToken: DomainToken = await callWithSilentRetry(
      () =>
        signInInteractor.fetchDomainToken({
          authorization: domainTokenScheme + " " + primaryCredential
        }),
      "GET"
    );
    Log.d(TAG, "refresh() profile-scoped mint succeeded");
    return domainToken;
  }

  /** Account-level walk: IP auto-login → bound device → DBS renewal. */
  private async refreshAccountToken(
    signInInteractor: SignInInteractor,
    configs: AuthConfigurationV3[] | undefined
  ): Promise<DomainToken> {
    Log.d(TAG, "refresh() attempting silent account refresh");
    const primaryCredential: string = await buildPrimaryCredentialJwt();

    const ipToken: DomainToken | null = await this.refreshViaIpAutoLogin(
      signInInteractor,
      configs,
      primaryCredential
    );
    if (ipToken !== null) {
      return ipToken;
    }

    const boundDeviceToken: DomainToken | null =
      await this.refreshViaBoundDevice(
        signInInteractor,
        configs,
        primaryCredential
      );
    if (boundDeviceToken !== null) {
      return boundDeviceToken;
    }

    try {
      return await this.refreshViaDbsRenewal(
        signInInteractor,
        configs,
        primaryCredential
      );
    } catch (dbsError: unknown) {
      Log.e(TAG, "refresh() DBS renewal failed", dbsError);
      throw new Error("Token refresh failed for all paths");
    }
  }

  /**
   * IP auto-login refresh: re-obtain IP JWT → bind → domainToken.
   * Returns null when this device did not sign in with IP auto-login, or the
   * path failed and the caller must try the next one.
   */
  private async refreshViaIpAutoLogin(
    signInInteractor: SignInInteractor,
    configs: AuthConfigurationV3[] | undefined,
    primaryCredential: string
  ): Promise<DomainToken | null> {
    const lastAuthContext = await DeviceModel.getLastAuthContext();
    if (
      lastAuthContext === undefined ||
      lastAuthContext === null ||
      lastAuthContext.authenticationType !==
        AuthenticationTypeV3.TivoIpAutoLoginIdp
    ) {
      return null;
    }

    const authenticationUrl: string | null =
      await resolveIpAutoLoginAuthenticationUrl(configs);
    if (authenticationUrl === null) {
      Log.w(
        TAG,
        "refresh() no IP auto-login authenticationUrl advertised, " +
          "trying fallback login"
      );
      return null;
    }

    try {
      // Never null here: the persisted context is for this very type, so it
      // backs up a configs response that carries no IP auto-login entry.
      const ipSchemes: TAuthorizationSchemes =
        (await resolveAuthorizationSchemes(
          AuthenticationTypeV3.TivoIpAutoLoginIdp,
          configs
        )) || lastAuthContext.authorizationSchemes;
      const msoName: string = DeviceModel.getTiVoSettings().getMsoName();
      const tokenResponse = await callWithSilentRetry(
        () => signInInteractor.ipAutologinToken(authenticationUrl, msoName),
        "POST"
      );
      const ipJwt: string = tokenResponse.jwt;
      if (typeof ipJwt === "string" && ipJwt.length > 0) {
        await DeviceModel.setIpJwtToken(ipJwt);

        await callWithSilentRetry(
          () =>
            signInInteractor.fetchBind({
              authorization:
                ipSchemes.bind + " " + primaryCredential + " " + ipJwt
            }),
          "GET"
        );

        const domainToken: DomainToken = await callWithSilentRetry(
          () =>
            signInInteractor.fetchDomainToken({
              authorization: ipSchemes.domainToken + " " + primaryCredential
            }),
          "GET"
        );
        Log.d(TAG, "refresh() IP auto-login refresh succeeded");
        return domainToken;
      }
    } catch (ipError: unknown) {
      Log.e(
        TAG,
        "refresh() IP auto-login path failed, trying bound-device path",
        ipError
      );
    }
    return null;
  }

  /**
   * Bound-device path: the device is already bound, so a new domain token is
   * minted from its primary credential alone. The persisted IDP JWT is only
   * read as evidence that an interactive sign-in happened on this device —
   * its presence, not its validity, since the JWT itself is never sent — with
   * the last auth context as a second witness.
   * Returns null when the device never signed in interactively or the call
   * failed and the caller must try the next path.
   */
  private async refreshViaBoundDevice(
    signInInteractor: SignInInteractor,
    configs: AuthConfigurationV3[] | undefined,
    primaryCredential: string
  ): Promise<DomainToken | null> {
    try {
      const lastAuthContext = await DeviceModel.getLastAuthContext();
      const signedInInteractively: boolean =
        (lastAuthContext !== undefined &&
          lastAuthContext !== null &&
          lastAuthContext.authenticationType ===
            AuthenticationTypeV3.TivoNativeIdp) ||
        (await DeviceModel.hasIdpJwtToken());
      if (signedInInteractively) {
        const domainTokenScheme: string =
          await resolveDomainTokenScheme(configs);
        const domainToken: DomainToken = await callWithSilentRetry(
          () =>
            signInInteractor.fetchDomainToken({
              authorization: domainTokenScheme + " " + primaryCredential
            }),
          "GET"
        );
        Log.d(TAG, "refresh() bound-device refresh succeeded");
        return domainToken;
      }
    } catch (boundDeviceError: unknown) {
      Log.e(
        TAG,
        "refresh() bound-device path failed, trying DBS renewal",
        boundDeviceError
      );
    }
    return null;
  }

  /**
   * DBS renewal path: DBS token → dbsJwtToken (no license plate, the device is
   * already bound) → bind → domainToken. This is the last path, so a failure
   * here fails the whole refresh.
   */
  private async refreshViaDbsRenewal(
    signInInteractor: SignInInteractor,
    configs: AuthConfigurationV3[] | undefined,
    primaryCredential: string
  ): Promise<DomainToken> {
    const dbsSchemes: TAuthorizationSchemes = await resolveDbsSchemes(configs);

    const dbsTokenResponse: DomainToken = await callWithSilentRetry(
      () =>
        signInInteractor.fetchDomainToken({
          authorization: dbsSchemes.domainToken + " " + primaryCredential
        }),
      "GET"
    );

    const msoName: string = DeviceModel.getTiVoSettings().getMsoName();
    const dlid: string = DeviceModel.getDeviceLoggingId();
    const dbsJwtResponse = await callWithSilentRetry(
      () =>
        signInInteractor.createDbsJwtToken(
          { msoName, deviceLoggingId: dlid },
          dbsTokenResponse.token
        ),
      "POST"
    );

    await callWithSilentRetry(
      () =>
        signInInteractor.fetchBind({
          authorization:
            dbsSchemes.bind +
            " " +
            primaryCredential +
            " " +
            dbsJwtResponse.token
        }),
      "GET"
    );

    const domainToken: DomainToken = await callWithSilentRetry(
      () =>
        signInInteractor.fetchDomainToken({
          authorization: dbsSchemes.domainToken + " " + primaryCredential
        }),
      "GET"
    );
    Log.d(TAG, "refresh() DBS renewal succeeded");
    return domainToken;
  }
}
